import axios from 'axios';
import { stringify } from 'querystring';

import { AddressType, AvCheckRequestBody, CmsPageContentType } from '@/types/av-check';

import { renderCmsContent, withRenderedCmsContent } from '@/utils';
import mockAvCheckResult from './mockAvCheckResult.json';

class ApiService {
  private readonly serviceUrl: string;

  constructor(serviceUrl: string) {
    this.serviceUrl = serviceUrl;
  }

  getEmptyResult(cmsCall: boolean, address?: AddressType){
    return {
      address: address,
      tariffOffers: [],
      maxBandwidth: { value: 0, text: '0 MBit/s', unit: 'MEGABIT_PER_SECOND' },
      ecommerceProductValues: [],
      overviewDisplayPrices: [],
      cmsCall: cmsCall,
    };
  }

  async getStoredResult(shopContext?: string) {
    const queryString = stringify({ shopContext });
    return axios.get(`${this.serviceUrl}/rest/availability/result${queryString ? `?${queryString}` : ''}`)
      .then((response) => {
        // Only for mock environment and test purpose
        if (process.env.mock) {
          return {
            address: mockAvCheckResult.matchedAddress,
            tariffOffers: mockAvCheckResult.tariffOffers,
            maxBandwidth: mockAvCheckResult.maxBandwidth,
            maxBandwidthForAccessTechnology: this.mapMaxBandwidthForAccessTechology(mockAvCheckResult.maxBandwidthForAccessTechnology),
            householdConfiguration: mockAvCheckResult.householdConfiguration,
            isCmsCall: mockAvCheckResult.cmsCall,
          };
        }

        if (!response.data) {
          return undefined;
        }

        return {
          address: response.data.matchedAddress,
          tariffOffers: withRenderedCmsContent(response.data.tariffOffers),
          maxBandwidth: response.data.maxBandwidth,
          maxBandwidthForAccessTechnology: this.mapMaxBandwidthForAccessTechology(response.data.maxBandwidthForAccessTechnology),
          ecommerceProductValues: response.data.ecommerceProductValues,
          householdConfiguration: response.data.householdConfiguration,
          isCmsCall: response.data.cmsCall,
          overviewDisplayPrices: response.data.overviewDisplayPrices,
        };
      });
  }

  async checkAvailability(
    requestBody: AvCheckRequestBody,
    shopContext?: string,
    selectedPack?: string,
    selectedRouter?: string,
    hardwareOfferName?: string,
  ) {
    const queryString = stringify({
      shopContext,
      selectedPack,
      selectedRouter,
      hardwareOfferName,
    });

    return axios.post(`${this.serviceUrl}/rest/availability/check${queryString ? `?${queryString}` : ''}`, requestBody)
      .then((response) => {
        // Only for mock environment and test purpose
        if (process.env.mock) {
          return {
            address: mockAvCheckResult.matchedAddress,
            tariffOffers: mockAvCheckResult.tariffOffers,
            maxBandwidth: mockAvCheckResult.maxBandwidth,
            maxBandwidthForAccessTechnology: this.mapMaxBandwidthForAccessTechology(mockAvCheckResult.maxBandwidthForAccessTechnology),
            householdConfiguration: mockAvCheckResult.householdConfiguration,
            isCmsCall: mockAvCheckResult.cmsCall,
          };
        }

        return {
          address: response.data.matchedAddress,
          tariffOffers: withRenderedCmsContent(response.data.tariffOffers),
          maxBandwidth: response.data.maxBandwidth,
          maxBandwidthForAccessTechnology: this.mapMaxBandwidthForAccessTechology(response.data.maxBandwidthForAccessTechnology),
          ecommerceProductValues: response.data.ecommerceProductValues,
          householdConfiguration: response.data.householdConfiguration,
          overviewDisplayPrices: response.data.overviewDisplayPrices,
          isCmsCall: response.data.cmsCall,
        };
      });
  }

  private mapMaxBandwidthForAccessTechology(maxBandwidthForAccessTechnologyFromResponse: any) {
    if (maxBandwidthForAccessTechnologyFromResponse) {
      return new Map(Object.entries(maxBandwidthForAccessTechnologyFromResponse));
    }
    return {};
  }

  async getPageValue() {
    return axios
      .get(`${this.serviceUrl}/rest/availability/page`)
      .then((result) => {
        const cmsContent: CmsPageContentType = {};
        Object.entries(result.data.cmsContent).forEach(([key, value]) => {
          cmsContent[key] = renderCmsContent((value as any));
        });

        return {
          meta: result.data.meta,
          enabledFeatures: result.data.enabledFeatures,
          cmsContent,
          address: result.data.address,
          index: result.data.index,
        };
      })
      .catch(() => undefined);
  }

  async getCmsSnippetSet(setName: string) {
    const cmsContent: CmsPageContentType = {};
    return axios
      .get(`${this.serviceUrl}/rest/cms/snippets?setName=${setName}`)
      .then((result) => {
        Object.entries(result.data).forEach(([key, value]) => {
          cmsContent[key] = renderCmsContent((value as any));
        });

        return cmsContent;
      })
      .catch(() => undefined);
  }

  async getCitiesByZipcode(zipCode: string, onSuccess?: (cities: string[]) => void) {
    return axios.get(`${this.serviceUrl}/rest/autocompletion/address/city/${zipCode}`)
      .then((response) => {
        if (onSuccess) {
          // Only for mock environment and test purpose
          if (process.env.mock) {
            return onSuccess(['Augsburg', 'Garching', 'Karlsfelf', 'München', 'Oberschleißheim', 'Pasing']);
          }
          return onSuccess(response.data);
        }

        return response.data;
      });
  }

  async getStreetsByCity(zipCode: string, city: string, onSuccess?: (streets: string[]) => void) {
    return axios.get(`${this.serviceUrl}/rest/autocompletion/address/street/${zipCode}/${city}`)
      .then((response) => {
        if (onSuccess) {
          if (process.env.mock) {
            const streets = {
              Augsburg: ['A-Street 1', 'A-Street 2', 'A-Street 3'],
              Garching: ['G-Street 1', 'G-Street 2', 'G-Street 3'],
              Karlsfeld: ['K-Street 1', 'K-Street 2', 'K-Street 3'],
              München: ['M-Street 1', 'M-Street 2', 'M-Street 3'],
              Oberschleissheim: ['O-Street 1', 'O-Street 2', 'O-Street 3'],
              Pasing: ['P-Street 1', 'P-Street 2', 'P-Street 3'],
            };
            return onSuccess((streets as any)[city]);
          }
          return onSuccess(response.data);
        }

        return response.data;
      });
  }
}

export default ApiService;
